import { useReducer, useState, useRef, useEffect, useContext } from "react";
import {
  Grid,
  GridItem,
  Button,
  Box,
  Flex,
  Stack,
  useColorModeValue as mode,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import shortid from "shortid";
import toast from "react-hot-toast";

// Components
import Preview from "./preview";
import Tabs from "./tabs";
import Editor from "./editor";
import Settings from "./settings";
import Thumbnails from "./thumbnails";
import Theme from "./theme";
import InputGenerator from "./input-generator";
import FontsLinks from "./fonts-links";

// State
import appReducer, { appInitialState } from "./utils/app-reducer";

// Types
import type { Carousel, StateWithId } from "types/carousel";

// Styles
import { layerStyle, variant } from "theme/names";

// Icons
import { ImFloppyDisk } from "react-icons/im";
import { BsPencil } from "react-icons/bs";
import { LuDownload } from "react-icons/lu";

// Utils
import { saveCarousel } from "controllers/carousel";
import { getAccount } from "utils/sessionHelper";
import { useSession } from "next-auth/react";
import { TweetContext } from "context/tweetContext";
import { uploadOnS3 } from "utils/uploadUtils";
import { sanitizeInput } from "utils/sanitizationHelpers";

const baseUrl = "https://us-central1-ez4cast.cloudfunctions.net";
const BUCKET_NAME = process.env.NEXT_PUBLIC_PROJECT_BUCKET;

const generatePDF = async (appState, session) => {
  const htmlRes = await fetch(`/api/generateCarouselHTML`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      appState,
    }),
  });
  let html;
  if (htmlRes?.ok) {
    const json = await htmlRes.json();
    html = json.html;
  } else {
    return new Error("Error generating HTML");
  }
  let res;
  try {
    res = await fetch(`${baseUrl}/linkedinCarousell-generatePDF`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        tokenuserid: session?.user.uid || "",
      },
      body: JSON.stringify({
        html,
        isInfluentLeader: true,
      }),
    });
  } catch (error) {
    return new Error("Error generating PDF");
  }
  return res;
};

export default function AICarousel({
  state,
  setCarousels,
}: {
  state?: StateWithId;
  setCarousels: (
    value: ((prevState: StateWithId[]) => StateWithId[]) | StateWithId[]
  ) => void;
}) {
  const { data: session } = useSession() ?? {};
  const [appState, setAppState] = useReducer(
    appReducer,
    state || appInitialState
  );
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isCreatingPost, setIsCreatingPost] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previewScale, setPreviewScale] = useState<number>(1);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const tweetContext = useContext<any>(TweetContext);
  const [selectedCarouselId, setselectedCarouselId] = useState<string>(
    appState?.carousels[0]?.id
  );
  let selectedCarouselIdx: number = appState?.carousels?.findIndex(
    (carousel) => carousel?.id === selectedCarouselId
  );
  selectedCarouselIdx = selectedCarouselIdx === -1 ? 0 : selectedCarouselIdx;
  const selectedCarousel: Carousel =
    appState?.carousels[selectedCarouselIdx] || appState?.carousels[0] || {};
  const isExistingCarousel: boolean = !!state && !!state.id;
  const [documentName, setDocumentName] = useState<string>("");
  const {
    isOpen: isDocModalOpen,
    onOpen: onOpenDocModal,
    onClose: onCloseDocModal,
  } = useDisclosure();

  // Load intial state from local storage
  useEffect(() => {
    if (!state) {
      const carouselVersion = "1.2.0";
      const storedVersion = localStorage.getItem("carousel-version");
      console.log("storedVersion", storedVersion);
      if (storedVersion !== carouselVersion) {
        localStorage.removeItem("carousel-state");
        localStorage.setItem("carousel-version", carouselVersion);
        return;
      }
      const localStateStr = localStorage.getItem("carousel-state");
      if (localStateStr) {
        const localState = JSON.parse(localStateStr);
        if (
          localState?.carousels?.length > 0 &&
          localState?.theme &&
          localState?.settings
        ) {
          setAppState({
            type: "LOAD_FROM_LOCAL_STORAGE",
            payload: localState,
          });
          console.log("Updated state from local storage");
          setselectedCarouselId(localState.carousels[0].id);
        }
      }
    }
  }, []);

  // Handle slide preview responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (previewRef.current) {
        const { width } = previewRef.current.getBoundingClientRect();
        setPreviewScale(width / 448);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const res = await generatePDF(appState, session);
      if (res?.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${appState.carousels[0].title}.pdf`;
        a.click();
      } else {
        throw new Error("Error generating PDF");
      }
    } catch (error) {
      toast.error(
        "Something went wrong while generating the PDF. Please try again."
      );
    }
    setIsGenerating(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const user = getAccount(session);
      const idUser = user.id;
      const newCarousel = await saveCarousel(appState, idUser);
      setCarousels((prevCarousels: StateWithId[]) => {
        const carouselIdx = prevCarousels.findIndex(
          (carousel: StateWithId) => carousel.id === newCarousel.id
        );
        if (carouselIdx === -1) {
          return [newCarousel, ...prevCarousels];
        }
        return prevCarousels.map((carousel: StateWithId, idx: number) =>
          idx === carouselIdx ? newCarousel : carousel
        );
      });
      toast.success("Carousel saved successfully!");
    } catch (error) {
      toast.error(
        "Something went wrong while saving the carousel. Please try again."
      );
    }

    setIsSaving(false);
  };

  const handleCreatePost = async () => {
    setIsCreatingPost(true);
    try {
      const res = await generatePDF(appState, session);
      if (res?.ok) {
        const blob = await res.blob();
        let id = shortid.generate().replace("-", "_");
        let title = sanitizeInput(documentName);
        if (title.includes("[") || title.includes("]")) {
          console.log("Title contains brackets, applying encoding");
          title = title.replace(/\[/g, "%5B").replace(/\]/g, "%5D");
        }

        const fileName = `doc-${title}-${id}`;
        const uploadFileName = `doc-${id}`;
        const pdfFile = new File([blob], fileName, {
          type: blob.type,
        });
        const params = {
          Bucket: BUCKET_NAME,
          Key: "userUpload/" + uploadFileName,
          Body: pdfFile,
          ACL: "public-read",
        };
        try {
          await uploadOnS3(params);
          const sanitizedTitle = sanitizeInput(appState.carousels[0].title);

          tweetContext.newTweet({
            text: `${sanitizedTitle}[img:${fileName}]`,
            files: [`${fileName}`],
          });
          tweetContext.open();
        } catch (error) {
          throw new Error(error);
        }
      } else {
        throw new Error("Error generating PDF");
      }
    } catch (error) {
      toast.error(
        "Something went wrong while generating the PDF. Please try again."
      );
    }
    setIsCreatingPost(false);
    onCloseDocModal();
  };

  if (isPreviewMode) {
    return (
      <>
        <FontsLinks settings={appState.settings} />
        <Box>
          <Flex justifyContent={"end"} mb={4} gap={2} maxW="container.xl">
            <Button variant="secondary" onClick={() => setIsPreviewMode(false)}>
              Exit Preview
            </Button>
          </Flex>
          <Grid
            gridAutoFlow={"column"}
            gridAutoColumns={"30rem"}
            overflowX={"scroll"}
            gap={2}
          >
            {appState.carousels
              .filter((carousel) => !carousel.invisible)
              .map((carousel, idx) => (
                <Box key={carousel.id}>
                  <Preview
                    content={carousel}
                    settings={appState.settings}
                    theme={appState.theme}
                    slideNo={appState?.carousels[0].invisible ? idx + 1 : idx}
                  />
                </Box>
              ))}
          </Grid>
        </Box>
      </>
    );
  }
  return (
    <>
      <FontsLinks settings={appState.settings} />
      {!isExistingCarousel && (
        <InputGenerator
          setAppState={setAppState}
          setselectedCarouselId={setselectedCarouselId}
        />
      )}
      <Grid
        templateRows={{
          md: "max-content 1fr max-content",
        }}
        templateColumns={{
          md: "2fr 3fr",
          xl: "30rem auto",
        }}
        gap={4}
      >
        <GridItem
          gridArea={{
            md: "1 / 1 / 3 / 2",
          }}
          w={"100%"}
          maxW={{ base: "20rem", md: "initial" }}
          mx="auto"
        >
          <Box layerStyle={layerStyle["bg.border.rounded"]} p="1rem">
            <div ref={previewRef}>
              <Preview
                content={selectedCarousel}
                settings={appState.settings}
                theme={appState.theme}
                slideNo={selectedCarouselIdx}
                scale={previewScale}
              />
            </div>
          </Box>
        </GridItem>
        <GridItem
          gridArea={{
            md: "1 / 2 / 2 / 3",
          }}
        >
          <Box layerStyle={layerStyle["bg.border.rounded"]}>
            <Tabs
              activeTab={appState.activeTab}
              onChange={(tab) =>
                setAppState({ type: "UPDATE_ACTIVE_TAB", payload: tab })
              }
              onReset={() => {
                setAppState({ type: "RESET", payload: null });
                setselectedCarouselId(appInitialState.carousels[0].id);
              }}
              setIsPreviewMode={setIsPreviewMode}
            />
          </Box>
        </GridItem>
        <GridItem
          gridArea={{
            md: "2 / 2 / 3 / 3",
          }}
        >
          <Box
            layerStyle={layerStyle["bg.border.rounded"]}
            h={"100%"}
            p="8"
            overflowY={"scroll"}
            maxH={"534px"}
          >
            {
              {
                Content: (
                  <Editor
                    content={selectedCarousel}
                    prevCarousels={appState.carousels.filter(
                      (_, idx) => idx < selectedCarouselIdx
                    )}
                    onChange={(key, value) =>
                      setAppState({
                        type: "UPDATE_CAROUSEL",
                        payload: {
                          id: selectedCarouselId,
                          key,
                          value,
                        },
                      })
                    }
                  />
                ),
                Settings: (
                  <Settings
                    settings={appState.settings}
                    onChange={(key, value) =>
                      setAppState({
                        type: "UPDATE_SETTINGS",
                        payload: {
                          key,
                          value,
                        },
                      })
                    }
                    onChangeFonts={(key, value) =>
                      setAppState({
                        type: "UPDATE_FONTS",
                        payload: {
                          key,
                          value,
                        },
                      })
                    }
                  />
                ),
                Theme: (
                  <Theme
                    theme={appState.theme}
                    onChangeColor={(key, value) =>
                      setAppState({
                        type: "UPDATE_COLORS",
                        payload: {
                          key,
                          value,
                        },
                      })
                    }
                    onChangeBackground={(key, value) =>
                      setAppState({
                        type: "UPDATE_BACKGROUND",
                        payload: {
                          key,
                          value,
                        },
                      })
                    }
                  />
                ),
              }[appState.activeTab]
            }
          </Box>
        </GridItem>
        <GridItem
          pb={4}
          mb={-4}
          overflowX={"scroll"}
          gridArea={{
            base: "1 / 1 / 2 / 2",
            md: "3 / 1 / 4 / 3",
          }}
        >
          <Thumbnails
            carousels={appState.carousels}
            selectedCarouselId={selectedCarouselId}
            setselectedCarouselId={setselectedCarouselId}
            addCarousel={(carousel: Carousel) => {
              setAppState({
                type: "ADD_CAROUSEL",
                payload: carousel,
              });
              setselectedCarouselId(carousel.id);
            }}
            reorderCarousels={(carousels) =>
              setAppState({
                type: "REORDER_CAROUSELS",
                payload: carousels,
              })
            }
            removeCarousel={(id) =>
              setAppState({
                type: "REMOVE_CAROUSEL",
                payload: id,
              })
            }
            updateCarouselVisibility={(id, invisible) =>
              setAppState({
                type: "UPDATE_CAROUSEL",
                payload: {
                  id,
                  key: "invisible",
                  value: invisible,
                },
              })
            }
            theme={appState.theme}
            settings={appState.settings}
          />
        </GridItem>
      </Grid>
      <Stack
        py={8}
        justify="center"
        align="center"
        direction={["column", "row"]}
      >
        {
          <Button
            leftIcon={<ImFloppyDisk />}
            variant="transparent"
            color={mode("primary.lightMode.400", "primary.darkMode.400")}
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save {isExistingCarousel ? "" : "for later"}
          </Button>
        }
        <Button
          variant={variant.Button.primary}
          leftIcon={<BsPencil />}
          px={8}
          mx={3}
          size="lg"
          onClick={() => {
            onOpenDocModal();
            if (appState.carousels[0]?.title) {
              const sanitizedTitle = sanitizeInput(appState.carousels[0].title);

              setDocumentName(sanitizedTitle);
            }
          }}
        >
          Create post
        </Button>
        <Button
          leftIcon={<LuDownload />}
          variant="transparent"
          color={mode("text.lightMode.light", "text.darkMode.light")}
          size="sm"
          onClick={handleDownload}
          isLoading={isGenerating}
        >
          Download PDF
        </Button>
      </Stack>
      <Modal
        isOpen={isDocModalOpen}
        onClose={onCloseDocModal}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Name your document</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl
              as="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreatePost();
              }}
            >
              <FormLabel>Document Title:</FormLabel>
              <Input
                placeholder={
                  appState?.carousels[0]?.title
                    ? sanitizeInput(appState.carousels[0].title)
                    : "My amazing carousel"
                }
                onChange={(e) => {
                  const sanitizedValue = sanitizeInput(e.target.value);

                  setDocumentName(sanitizedValue);
                }}
                value={sanitizeInput(documentName)}

              // defaultValue={
              //   appState?.carousels[0]?.title
              //     ? xss(appState.carousels[0].title, {
              //         whiteList: {}, // This removes all HTML tags
              //         stripIgnoreTag: true,
              //         stripIgnoreTagBody: ["script"],
              //       })
              //         .replace(/&gt;/g, ">")
              //         .replace(/&lt;/g, "<")
              //         .replace(/&amp;/g, "&")
              //     : undefined
              // }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              variant={variant.Button.primary}
              isLoading={isCreatingPost}
              onClick={handleCreatePost}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
